import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { dashboardApi } from "../services/api";
import {
  FiClock,
  FiArrowLeft,
  FiSearch,
  FiFilter,
  FiAlertTriangle,
  FiCheckCircle,
  FiXCircle,
  FiRefreshCw,
  FiX,
  FiChevronLeft,
  FiChevronRight,
  FiBox,
} from "react-icons/fi";
import { toast } from "react-toastify";

const WarrantyPage = () => {
  const [warranties, setWarranties] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all"); // 'all', 'expiring', 'expired', 'active'

  // Pagination (10 / 20 / 50 per page)
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const fetchWarranties = useCallback(async () => {
    try {
      setLoading(true);
      // Fetch only items that are in stock
      const res = await dashboardApi.getWarrantyReport({ status: "all", in_stock_only: "true" });
      setWarranties(res.data || []);
    } catch (err) {
      toast.error(err.message || "Failed to load warranty records");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchWarranties();
  }, [fetchWarranties]);

  // Strictly filter only items that are in stock (exclude sold items and 0 stock items)
  const inStockWarranties = useMemo(() => {
    return warranties.filter((w) => {
      // Exclude if marked as sold
      if (w.is_sale === true || w.is_sale === 1 || w.is_sale === "1" || w.record_type === "sale") {
        return false;
      }
      // Exclude if current_stock is 0 or less
      if (w.current_stock !== undefined && w.current_stock !== null && parseInt(w.current_stock, 10) <= 0) {
        return false;
      }
      return true;
    });
  }, [warranties]);

  // KPI Calculations (based on in-stock warranties)
  const stats = useMemo(() => {
    let expired = 0;
    let expiring = 0;
    let active = 0;

    inStockWarranties.forEach((w) => {
      if (w.warranty_status === "Expired") expired++;
      else if (w.warranty_status === "Expiring Soon") expiring++;
      else active++;
    });

    return {
      total: inStockWarranties.length,
      expired,
      expiring,
      active,
    };
  }, [inStockWarranties]);

  // Filtered Warranties (Status + Search)
  const filteredWarranties = useMemo(() => {
    return inStockWarranties.filter((w) => {
      // Status filter
      if (statusFilter === "expiring" && w.warranty_status !== "Expiring Soon") return false;
      if (statusFilter === "expired" && w.warranty_status !== "Expired") return false;
      if (statusFilter === "active" && w.warranty_status !== "Active") return false;

      // Search filter
      if (search.trim()) {
        const q = search.toLowerCase().trim();
        const matchesItem = w.item_name?.toLowerCase().includes(q);
        const matchesSku = w.sku?.toLowerCase().includes(q);
        const matchesParty = w.party_name?.toLowerCase().includes(q);
        const matchesCat = w.category_name?.toLowerCase().includes(q);
        const matchesSub = w.subcategory_name?.toLowerCase().includes(q);
        const matchesSn = w.serial_number?.toLowerCase().includes(q);
        const matchesPn = w.part_number?.toLowerCase().includes(q);
        if (!matchesItem && !matchesSku && !matchesParty && !matchesCat && !matchesSub && !matchesSn && !matchesPn) {
          return false;
        }
      }

      return true;
    });
  }, [inStockWarranties, statusFilter, search]);

  // Reset to page 1 whenever filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter, search, pageSize]);

  // Pagination Calculations
  const totalPages = Math.max(1, Math.ceil(filteredWarranties.length / pageSize));
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = Math.min(startIndex + pageSize, filteredWarranties.length);
  const paginatedWarranties = useMemo(() => {
    return filteredWarranties.slice(startIndex, endIndex);
  }, [filteredWarranties, startIndex, endIndex]);

  return (
    <div className="fade-in">
      {/* Header & Back Navigation */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 16,
          marginBottom: 24,
        }}
      >
        <div>
          <Link
            to="/stock"
            className="no-print"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              fontSize: "0.84rem",
              fontWeight: 700,
              color: "var(--primary)",
              textDecoration: "none",
              marginBottom: 8,
            }}
          >
            <FiArrowLeft size={16} /> Back to Stock Overview
          </Link>

          <h2 style={{ fontSize: "1.45rem", fontWeight: 800, color: "var(--navy)" }}>
            Warranty Expiry Tracker{" "}
            <span style={{ fontSize: "0.85rem", color: "var(--warning)", fontWeight: 700 }}>
              In-Stock Items Only
            </span>
          </h2>
          <p style={{ fontSize: "0.85rem", color: "var(--slate-500)", marginTop: 2 }}>
            Track and audit warranties for items currently available on-hand in inventory.
          </p>
        </div>

        <button
          onClick={fetchWarranties}
          title="Refresh Warranty Records"
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            padding: "9px 15px",
            borderRadius: "var(--radius-md)",
            border: "1px solid var(--slate-300)",
            backgroundColor: "#fff",
            color: "var(--slate-700)",
            fontSize: "0.86rem",
            cursor: "pointer",
            fontWeight: 700,
            boxShadow: "var(--shadow-sm)",
          }}
        >
          <FiRefreshCw size={14} /> Refresh
        </button>
      </div>

      {/* KPI Cards: Filter by Status */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))",
          gap: 16,
          marginBottom: 24,
        }}
      >
        {/* Total In-Stock */}
        <div
          onClick={() => setStatusFilter("all")}
          style={{
            backgroundColor: "#fff",
            borderRadius: "var(--radius-lg)",
            padding: "20px 22px",
            border: statusFilter === "all" ? "2px solid var(--navy)" : "1px solid var(--slate-200)",
            boxShadow: statusFilter === "all" ? "0 4px 14px rgba(15, 23, 42, 0.1)" : "var(--shadow-sm)",
            display: "flex",
            alignItems: "center",
            gap: 16,
            cursor: "pointer",
            transition: "all 0.15s ease",
          }}
        >
          <div
            style={{
              width: 50,
              height: 50,
              borderRadius: 14,
              backgroundColor: "var(--slate-100)",
              color: "var(--navy)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <FiBox size={24} />
          </div>
          <div>
            <div style={{ fontSize: "0.78rem", color: "var(--slate-500)", fontWeight: 700, textTransform: "uppercase" }}>
              In-Stock Tracked
            </div>
            <div style={{ fontSize: "1.7rem", fontWeight: 800, color: "var(--navy)", marginTop: 2 }}>
              {stats.total}{" "}
              <span style={{ fontSize: "0.82rem", fontWeight: 600, color: "var(--slate-400)" }}>items</span>
            </div>
          </div>
        </div>

        {/* Expiring Soon */}
        <div
          onClick={() => setStatusFilter("expiring")}
          style={{
            backgroundColor: "#fff",
            borderRadius: "var(--radius-lg)",
            padding: "20px 22px",
            border: statusFilter === "expiring" ? "2px solid var(--warning)" : "1px solid var(--slate-200)",
            boxShadow: statusFilter === "expiring" ? "0 4px 14px rgba(217, 119, 6, 0.15)" : "var(--shadow-sm)",
            display: "flex",
            alignItems: "center",
            gap: 16,
            cursor: "pointer",
            transition: "all 0.15s ease",
          }}
        >
          <div
            style={{
              width: 50,
              height: 50,
              borderRadius: 14,
              backgroundColor: "var(--warning-bg)",
              color: "var(--warning)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <FiAlertTriangle size={24} />
          </div>
          <div>
            <div style={{ fontSize: "0.78rem", color: "var(--slate-500)", fontWeight: 700, textTransform: "uppercase" }}>
              Expiring Soon (≤30 Days)
            </div>
            <div style={{ fontSize: "1.7rem", fontWeight: 800, color: "var(--warning)", marginTop: 2 }}>
              {stats.expiring}{" "}
              <span style={{ fontSize: "0.82rem", fontWeight: 600, color: "var(--slate-400)" }}>items</span>
            </div>
          </div>
        </div>

        {/* Expired */}
        <div
          onClick={() => setStatusFilter("expired")}
          style={{
            backgroundColor: "#fff",
            borderRadius: "var(--radius-lg)",
            padding: "20px 22px",
            border: statusFilter === "expired" ? "2px solid var(--danger)" : "1px solid var(--slate-200)",
            boxShadow: statusFilter === "expired" ? "0 4px 14px rgba(220, 38, 38, 0.15)" : "var(--shadow-sm)",
            display: "flex",
            alignItems: "center",
            gap: 16,
            cursor: "pointer",
            transition: "all 0.15s ease",
          }}
        >
          <div
            style={{
              width: 50,
              height: 50,
              borderRadius: 14,
              backgroundColor: "var(--danger-bg)",
              color: "var(--danger)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <FiXCircle size={24} />
          </div>
          <div>
            <div style={{ fontSize: "0.78rem", color: "var(--slate-500)", fontWeight: 700, textTransform: "uppercase" }}>
              Expired Warranties
            </div>
            <div style={{ fontSize: "1.7rem", fontWeight: 800, color: "var(--danger)", marginTop: 2 }}>
              {stats.expired}{" "}
              <span style={{ fontSize: "0.82rem", fontWeight: 600, color: "var(--slate-400)" }}>items</span>
            </div>
          </div>
        </div>

        {/* Active Warranties */}
        <div
          onClick={() => setStatusFilter("active")}
          style={{
            backgroundColor: "#fff",
            borderRadius: "var(--radius-lg)",
            padding: "20px 22px",
            border: statusFilter === "active" ? "2px solid var(--success)" : "1px solid var(--slate-200)",
            boxShadow: statusFilter === "active" ? "0 4px 14px rgba(16, 185, 129, 0.15)" : "var(--shadow-sm)",
            display: "flex",
            alignItems: "center",
            gap: 16,
            cursor: "pointer",
            transition: "all 0.15s ease",
          }}
        >
          <div
            style={{
              width: 50,
              height: 50,
              borderRadius: 14,
              backgroundColor: "var(--success-bg)",
              color: "var(--success)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <FiCheckCircle size={24} />
          </div>
          <div>
            <div style={{ fontSize: "0.78rem", color: "var(--slate-500)", fontWeight: 700, textTransform: "uppercase" }}>
              Active Warranties
            </div>
            <div style={{ fontSize: "1.7rem", fontWeight: 800, color: "var(--success)", marginTop: 2 }}>
              {stats.active}{" "}
              <span style={{ fontSize: "0.82rem", fontWeight: 600, color: "var(--slate-400)" }}>items</span>
            </div>
          </div>
        </div>
      </div>

      {/* Filter / Search Bar */}
      <div
        className="no-print"
        style={{
          backgroundColor: "#fff",
          borderRadius: "var(--radius-lg)",
          padding: "16px 20px",
          border: "1px solid var(--slate-200)",
          boxShadow: "var(--shadow-sm)",
          marginBottom: 20,
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 14,
        }}
      >
        {/* Status Dropdown */}
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <FiFilter size={16} color="var(--slate-500)" />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            style={{
              padding: "9px 12px",
              borderRadius: "var(--radius-md)",
              border: "1px solid var(--slate-300)",
              fontSize: "0.86rem",
              backgroundColor: "#fff",
              fontWeight: 600,
              color: "var(--slate-700)",
            }}
          >
            <option value="all">All Warranty Statuses</option>
            <option value="expiring">Expiring Soon (≤30 Days)</option>
            <option value="expired">Expired Warranties</option>
            <option value="active">Active Warranties</option>
          </select>
        </div>

        {/* Search */}
        <div style={{ position: "relative", flex: 1, minWidth: 260 }}>
          <FiSearch
            size={16}
            style={{
              position: "absolute",
              left: 12,
              top: "50%",
              transform: "translateY(-50%)",
              color: "var(--slate-400)",
            }}
          />
          <input
            type="text"
            placeholder="Search item name, SKU, serial #, part #, vendor..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{
              width: "100%",
              padding: "9px 34px 9px 36px",
              borderRadius: "var(--radius-md)",
              border: "1px solid var(--slate-300)",
              fontSize: "0.86rem",
              outline: "none",
              boxSizing: "border-box",
            }}
          />
          {search && (
            <button
              onClick={() => setSearch("")}
              style={{
                position: "absolute",
                right: 10,
                top: "50%",
                transform: "translateY(-50%)",
                background: "none",
                border: "none",
                cursor: "pointer",
                color: "var(--slate-400)",
                display: "flex",
                alignItems: "center",
              }}
            >
              <FiX size={15} />
            </button>
          )}
        </div>

        {/* In-Stock Indicator Tag */}
        <span
          style={{
            fontSize: "0.78rem",
            fontWeight: 700,
            backgroundColor: "var(--primary-light, #ECFDF5)",
            color: "var(--primary, #064E3B)",
            border: "1px solid rgba(6, 78, 59, 0.2)",
            padding: "6px 12px",
            borderRadius: "var(--radius-md)",
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
          }}
        >
          <span style={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: "var(--primary, #064E3B)" }} />
          Filter: In-Stock Only
        </span>

        {/* Reset Filter Button */}
        {(statusFilter !== "all" || search) && (
          <button
            onClick={() => {
              setStatusFilter("all");
              setSearch("");
            }}
            style={{
              padding: "9px 14px",
              borderRadius: "var(--radius-md)",
              border: "1px solid var(--slate-200)",
              backgroundColor: "var(--slate-100)",
              color: "var(--slate-700)",
              fontSize: "0.85rem",
              fontWeight: 700,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: 5,
            }}
          >
            <FiX size={14} /> Reset
          </button>
        )}
      </div>

      {/* Warranty Table */}
      <div
        style={{
          backgroundColor: "#fff",
          borderRadius: "var(--radius-lg)",
          border: "1px solid var(--slate-200)",
          boxShadow: "var(--shadow-sm)",
          overflow: "hidden",
        }}
      >
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.88rem" }}>
            <thead>
              <tr style={{ backgroundColor: "var(--slate-50)", borderBottom: "1px solid var(--slate-200)", textAlign: "left" }}>
                <th style={{ padding: "14px 18px", color: "var(--slate-600)", fontWeight: 600 }}>#</th>
                <th style={{ padding: "14px 18px", color: "var(--slate-600)", fontWeight: 600 }}>Item & SKU</th>
                <th style={{ padding: "14px 18px", color: "var(--slate-600)", fontWeight: 600 }}>Serial / Part #</th>
                <th style={{ padding: "14px 18px", color: "var(--slate-600)", fontWeight: 600 }}>Vendor (Supplier)</th>
                <th style={{ padding: "14px 18px", color: "var(--slate-600)", fontWeight: 600 }}>Procure Date</th>
                <th style={{ padding: "14px 18px", color: "var(--slate-600)", fontWeight: 600 }}>Warranty Start</th>
                <th style={{ padding: "14px 18px", color: "var(--slate-600)", fontWeight: 600 }}>Warranty End</th>
                <th style={{ padding: "14px 18px", color: "var(--slate-600)", fontWeight: 600 }}>Days Left</th>
                <th style={{ padding: "14px 18px", color: "var(--slate-600)", fontWeight: 600 }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={9} style={{ padding: 40, textAlign: "center", color: "var(--slate-400)" }}>
                    Loading in-stock warranty records...
                  </td>
                </tr>
              ) : filteredWarranties.length === 0 ? (
                <tr>
                  <td colSpan={9} style={{ padding: 40, textAlign: "center", color: "var(--slate-400)" }}>
                    <div style={{ fontWeight: 700, color: "var(--navy)", marginBottom: 4 }}>
                      No warranty records found
                    </div>
                    <div style={{ fontSize: "0.82rem" }}>
                      No in-stock items matching the current warranty filters.
                    </div>
                  </td>
                </tr>
              ) : (
                paginatedWarranties.map((w, idx) => {
                  const isExpired = w.warranty_status === "Expired";
                  const isExpiring = w.warranty_status === "Expiring Soon";

                  return (
                    <tr
                      key={`${w.id}-${idx}`}
                      style={{
                        borderBottom: "1px solid var(--slate-100)",
                        backgroundColor: isExpired ? "#FEF2F2" : isExpiring ? "#FFFBEB" : "transparent",
                        transition: "background 0.15s",
                      }}
                      onMouseEnter={(e) => {
                        if (!isExpired && !isExpiring) e.currentTarget.style.backgroundColor = "var(--slate-50)";
                      }}
                      onMouseLeave={(e) => {
                        if (!isExpired && !isExpiring) e.currentTarget.style.backgroundColor = "transparent";
                      }}
                    >
                      <td style={{ padding: "14px 18px", color: "var(--slate-400)", fontWeight: 600 }}>
                        {startIndex + idx + 1}
                      </td>

                      {/* Item & SKU */}
                      <td style={{ padding: "14px 18px" }}>
                        <div style={{ fontWeight: 700, color: "var(--navy)" }}>{w.item_name}</div>
                        <div style={{ fontSize: "0.75rem", fontFamily: "monospace", color: "var(--slate-500)", marginTop: 2 }}>
                          SKU: <strong style={{ color: "var(--slate-700)" }}>{w.sku || "—"}</strong>
                        </div>
                      </td>

                      {/* Serial / Part # */}
                      <td style={{ padding: "14px 18px" }}>
                        <div style={{ fontFamily: "monospace", fontSize: "0.8rem", color: "var(--navy)", fontWeight: 600 }}>
                          SN: {w.serial_number || <span style={{ color: "var(--slate-400)" }}>—</span>}
                        </div>
                        <div style={{ fontFamily: "monospace", fontSize: "0.76rem", color: "var(--slate-500)" }}>
                          PN: {w.part_number || <span style={{ color: "var(--slate-400)" }}>—</span>}
                        </div>
                      </td>

                      {/* Vendor (Supplier) */}
                      <td style={{ padding: "14px 18px" }}>
                        <div style={{ fontWeight: 700, color: "var(--slate-800)" }}>{w.party_name || "—"}</div>
                        {w.party_contact && (
                          <div style={{ fontSize: "0.74rem", color: "var(--slate-400)" }}>{w.party_contact}</div>
                        )}
                      </td>

                      {/* Procure Date */}
                      <td style={{ padding: "14px 18px", color: "var(--slate-600)" }}>
                        {w.transaction_date ? new Date(w.transaction_date).toLocaleDateString() : "—"}
                      </td>

                      {/* Warranty Start */}
                      <td style={{ padding: "14px 18px", color: "var(--slate-600)" }}>
                        {w.warranty_start_date ? new Date(w.warranty_start_date).toLocaleDateString() : "—"}
                      </td>

                      {/* Warranty End */}
                      <td style={{ padding: "14px 18px", fontWeight: 700, color: "var(--navy)" }}>
                        {w.warranty_end_date ? new Date(w.warranty_end_date).toLocaleDateString() : "—"}
                      </td>

                      {/* Days Left */}
                      <td style={{ padding: "14px 18px" }}>
                        <span
                          style={{
                            fontSize: "0.92rem",
                            fontWeight: 800,
                            color: isExpired ? "var(--danger)" : isExpiring ? "var(--warning)" : "var(--slate-700)",
                          }}
                        >
                          {w.days_left !== null && w.days_left !== undefined
                            ? isExpired
                              ? `Expired (${Math.abs(w.days_left)}d ago)`
                              : `${w.days_left} days left`
                            : "—"}
                        </span>
                      </td>

                      {/* Status */}
                      <td style={{ padding: "14px 18px" }}>
                        <span
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 4,
                            fontSize: "0.75rem",
                            padding: "4px 10px",
                            borderRadius: 12,
                            fontWeight: 800,
                            backgroundColor: isExpired
                              ? "var(--danger-bg)"
                              : isExpiring
                              ? "var(--warning-bg)"
                              : "var(--success-bg)",
                            color: isExpired ? "var(--danger)" : isExpiring ? "var(--warning)" : "var(--success)",
                          }}
                        >
                          {isExpired && <FiXCircle size={12} />}
                          {isExpiring && <FiAlertTriangle size={12} />}
                          {!isExpired && !isExpiring && <FiCheckCircle size={12} />}
                          {w.warranty_status}
                        </span>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination & Summary Bar (10 / 20 / 50 per page) */}
        {!loading && filteredWarranties.length > 0 && (
          <div
            style={{
              padding: "14px 18px",
              borderTop: "1px solid var(--slate-200)",
              backgroundColor: "var(--slate-50)",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 12,
              fontSize: "0.84rem",
              color: "var(--slate-600)",
            }}
          >
            <div>
              Showing <strong>{startIndex + 1}</strong> to <strong>{endIndex}</strong> of{" "}
              <strong>{filteredWarranties.length}</strong> in-stock records
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              {/* Rows Per Page Selector (10 / 20 / 50) */}
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <span>Rows:</span>
                <select
                  value={pageSize}
                  onChange={(e) => setPageSize(Number(e.target.value))}
                  style={{
                    padding: "4px 8px",
                    borderRadius: 6,
                    border: "1px solid var(--slate-300)",
                    fontSize: "0.82rem",
                    backgroundColor: "#fff",
                    fontWeight: 600,
                  }}
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                </select>
              </div>

              {/* Prev Button */}
              <button
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                  padding: "5px 12px",
                  borderRadius: 6,
                  border: "1px solid var(--slate-300)",
                  backgroundColor: currentPage === 1 ? "var(--slate-100)" : "#fff",
                  color: currentPage === 1 ? "var(--slate-400)" : "var(--slate-700)",
                  cursor: currentPage === 1 ? "not-allowed" : "pointer",
                  fontWeight: 600,
                  fontSize: "0.82rem",
                }}
              >
                <FiChevronLeft size={15} /> Prev
              </button>

              {/* Page Number Indicator */}
              <span style={{ fontWeight: 700, color: "var(--navy)" }}>
                Page {currentPage} of {totalPages}
              </span>

              {/* Next Button */}
              <button
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                  padding: "5px 12px",
                  borderRadius: 6,
                  border: "1px solid var(--slate-300)",
                  backgroundColor: currentPage === totalPages ? "var(--slate-100)" : "#fff",
                  color: currentPage === totalPages ? "var(--slate-400)" : "var(--slate-700)",
                  cursor: currentPage === totalPages ? "not-allowed" : "pointer",
                  fontWeight: 600,
                  fontSize: "0.82rem",
                }}
              >
                Next <FiChevronRight size={15} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default WarrantyPage;
